import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invoiceContract } from "@common/contracts/invoice.contract";
import { billContract } from "@common/contracts/bill.contract";
import {
    AccrualMatchDisplay,
    AccrualReviewApproveResponse,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import {
    TRANSACTION_DETAILS_QUERY_KEY,
    useTransactionDetailsQuery,
} from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { getTransactionDetails } from "@lib/transactions";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useScope } from "@state/scopeContext.lib";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useHasAccrualLedger } from "@api/ledger.api";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import {
    applyApproveToAccrualReviewCache,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { AccrualMatchProvider } from "./AccrualMatchContext";
import { AccrualMatchPage } from "./AccrualMatchPage";
import { EditAllocationDrawer } from "./EditAllocationDrawer";
import {
    accrualMatchedDocumentIds,
    linkedDocumentSuggestions,
} from "./linkedDocumentSuggestions";
import {
    billToMatchableItem,
    invoiceToMatchableItem,
    transactionToMatchableItem,
} from "./mappers";
import { MatchableItem, MatchableTransaction, MatchItemType } from "./types";
import styles from "./TransactionMatchDrawer.module.scss";

const invoiceClient = getBackendAPIClient(invoiceContract, undefined, {
    jsonQuery: true,
});
const billClient = getBackendAPIClient(billContract, undefined, {
    jsonQuery: true,
});

interface TransactionMatchDrawerProps {
    transactionId: number;
    open: boolean;
    onClose: () => void;
    suggestedItems?: MatchableItem[];
}

export const TransactionMatchDrawer: React.FC<TransactionMatchDrawerProps> = ({
    transactionId,
    open,
    onClose,
    suggestedItems,
}) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: [TRANSACTION_DETAILS_QUERY_KEY, transactionId],
        queryFn: () => getTransactionDetails(transactionId),
        enabled: open,
    });

    const transaction = data?.transaction;
    const existingMatches = transaction?.accrualMatches ?? [];

    // PRO-9381: a transfer pair lives on transaction.pairedTransferId until
    // the user confirms it in this drawer (which is when the AccrualMatch
    // row gets written). Fetch the partner so we can seed the command island
    // with it. Loading the partner from the same endpoint as the source
    // (rather than reading the embedded `pairedTransfer` summary) gives us
    // the partner's full `entity`, so cross-entity transfer pairs render
    // their own entity badge instead of inheriting the source's.
    //
    // If the partner fetch errors out (deleted record, transient failure),
    // settle on `isError` rather than spinning forever — the user can still
    // pick a match manually via the Suggested or search panels.
    const pairedTransferId = transaction?.pairedTransferId ?? null;
    const paired = useTransactionDetailsQuery(pairedTransferId);
    const isPendingPairedSettled =
        pairedTransferId === null ||
        paired.transaction !== undefined ||
        paired.isError;
    const pendingPairedTransaction = paired.transaction ?? null;

    if (transaction && isPendingPairedSettled) {
        return (
            <TransactionMatchDrawerContent
                transaction={transaction}
                transactionId={transactionId}
                open={open}
                onClose={onClose}
                suggestedItems={suggestedItems}
                existingMatches={existingMatches}
                pendingPairedTransaction={pendingPairedTransaction}
            />
        );
    }

    return (
        <Drawer open={open} onClose={onClose} size="fullScreen" noPadding>
            {(isLoading || (transaction && !isPendingPairedSettled)) && (
                <Loader />
            )}
            {isError && <EmptyState header="Transaction not found" />}
        </Drawer>
    );
};

interface TransactionMatchDrawerTitleProps {
    hasAccrualLedger: boolean;
}
const TransactionMatchDrawerTitle: React.FC<
    TransactionMatchDrawerTitleProps
> = ({ hasAccrualLedger }) => (
    <span className={styles.drawerTitle}>
        {hasAccrualLedger ? "Find the match" : "Match transaction"}
    </span>
);

interface ContentProps {
    transaction: MatchableTransaction;
    transactionId: number;
    open: boolean;
    onClose: () => void;
    suggestedItems?: MatchableItem[];
    existingMatches: AccrualMatchDisplay[];
    pendingPairedTransaction: MatchableTransaction | null;
}

function useEnrichedSuggestedItems(
    suggestedItems: MatchableItem[] | undefined,
    currentTxMatchAmounts: ReadonlyMap<string, number>,
): MatchableItem[] | undefined {
    const { workspaceId } = useScope();

    const invoiceIds = useMemo(
        () =>
            (suggestedItems ?? [])
                .filter((i) => i.type === MatchItemType.INVOICE)
                .map((i) => i.id.replace(/^inv-/, "")),
        [suggestedItems],
    );

    const billIds = useMemo(
        () =>
            (suggestedItems ?? [])
                .filter((i) => i.type === MatchItemType.BILL)
                .map((i) => i.id.replace(/^bill-/, "")),
        [suggestedItems],
    );

    const hasIds = invoiceIds.length > 0 || billIds.length > 0;

    const invoicesQuery = useQuery({
        queryKey: ["accrual-match-suggested-invoices", invoiceIds],
        queryFn: async () => {
            const result = await invoiceClient.getInvoices({
                params: { workspaceId: workspaceId! },
                query: { filters: { ids: invoiceIds }, page: 0, limit: 100 },
            });
            return result.body.data;
        },
        enabled: !!workspaceId && invoiceIds.length > 0,
    });

    const billsQuery = useQuery({
        queryKey: ["accrual-match-suggested-bills", billIds],
        queryFn: async () => {
            const result = await billClient.getBills({
                params: { workspaceId: workspaceId! },
                query: { filters: { ids: billIds }, page: 0, limit: 100 },
            });
            return result.body.data;
        },
        enabled: !!workspaceId && billIds.length > 0,
    });

    return useMemo(() => {
        if (!hasIds) {
            return suggestedItems;
        }

        const isLoading =
            (invoiceIds.length > 0 && invoicesQuery.isLoading) ||
            (billIds.length > 0 && billsQuery.isLoading);

        if (isLoading) {
            return suggestedItems;
        }

        const enrichedInvoices = (invoicesQuery.data ?? []).map((invoice) =>
            invoiceToMatchableItem(invoice, currentTxMatchAmounts),
        );
        const enrichedBills = (billsQuery.data ?? []).map((bill) =>
            billToMatchableItem(bill, currentTxMatchAmounts),
        );
        const enrichedMap = new Map(
            [...enrichedInvoices, ...enrichedBills].map((i) => [i.id, i]),
        );

        return (suggestedItems ?? []).map(
            (item) => enrichedMap.get(item.id) ?? item,
        );
    }, [
        hasIds,
        suggestedItems,
        invoiceIds.length,
        billIds.length,
        invoicesQuery.isLoading,
        invoicesQuery.data,
        billsQuery.isLoading,
        currentTxMatchAmounts,
        billsQuery.data,
    ]);
}

function buildStubsFromExistingMatches(
    existingMatches: AccrualMatchDisplay[],
    sourceTransaction: MatchableTransaction,
): MatchableItem[] {
    const sourceEntity = sourceTransaction.entity;
    return existingMatches
        .map((match): MatchableItem | null => {
            if (match.invoice) {
                // Seed openAmount with this transaction's contribution so
                // the AccrualMatchFooter amount-matched check is accurate
                // even before the full invoice payload is enriched.
                return {
                    id: `inv-${match.invoice.id}`,
                    type: MatchItemType.INVOICE,
                    date: match.invoice.createdAt ?? new Date(),
                    counterpartyName: match.invoice.counterpartyName ?? null,
                    description: null,
                    accountIds: [],
                    classIds: [],
                    amount: match.invoice.total,
                    openAmount: match.amount,
                    entityId: sourceEntity.id,
                    entity: sourceEntity,
                };
            }
            if (match.bill) {
                return {
                    id: `bill-${match.bill.id}`,
                    type: MatchItemType.BILL,
                    date: match.bill.createdAt ?? new Date(),
                    counterpartyName: match.bill.counterpartyName ?? null,
                    description: null,
                    accountIds: [],
                    classIds: [],
                    amount: match.bill.total,
                    openAmount: match.amount,
                    entityId: sourceEntity.id,
                    entity: sourceEntity,
                };
            }
            if (match.pairedTransaction) {
                return {
                    id: `tx-${match.pairedTransaction.id}`,
                    type: MatchItemType.TRANSACTION,
                    date: match.pairedTransaction.date,
                    counterpartyName: null,
                    description:
                        match.pairedTransaction.displayDescription ?? null,
                    accountIds: [],
                    classIds: [],
                    amount: match.pairedTransaction.amount,
                    openAmount: null,
                    entityId: sourceEntity.id,
                    entity: sourceEntity,
                };
            }
            return null;
        })
        .filter((item): item is MatchableItem => item !== null);
}

const TransactionMatchDrawerContent: React.FC<ContentProps> = ({
    transaction,
    transactionId,
    open,
    onClose,
    suggestedItems,
    existingMatches,
    pendingPairedTransaction,
}) => {
    const queryClient = useQueryClient();

    const entityIdForGLAccount = useEntityIdForGLAccount(transaction);
    const hasAccrualLedger = useHasAccrualLedger(entityIdForGLAccount);
    const ledgerBasis = hasAccrualLedger
        ? AccountingLedgerBasis.accruals
        : AccountingLedgerBasis.cash;
    const { enabled: isGlFirst } = useIsGlFirst();

    const currentTxMatchAmounts = useMemo(() => {
        const map = new Map<string, number>();
        for (const match of existingMatches) {
            for (const line of match.lineItems ?? []) {
                map.set(
                    line.lineItemId,
                    (map.get(line.lineItemId) ?? 0) + line.amount,
                );
            }
        }
        return map;
    }, [existingMatches]);

    const documentSuggestions = useMemo(
        () =>
            linkedDocumentSuggestions({
                matches: transaction.transactionMatches,
                entity: transaction.entity,
                fallbackAmount: Math.abs(transaction.amount),
                fallbackDate: transaction.date,
                excludedIds: accrualMatchedDocumentIds(existingMatches),
            }),
        [transaction, existingMatches],
    );

    const suggestedWithDocuments = useMemo(() => {
        if (documentSuggestions.length === 0) {
            return suggestedItems;
        }
        const incoming = suggestedItems ?? [];
        const incomingIds = new Set(incoming.map((item) => item.id));
        const extras = documentSuggestions.filter(
            (item) => !incomingIds.has(item.id),
        );
        return [...incoming, ...extras];
    }, [suggestedItems, documentSuggestions]);

    const { mergedInitialItems, persistedItemIds } = useMemo(() => {
        const existingStubs = buildStubsFromExistingMatches(
            existingMatches,
            transaction,
        );

        // PRO-9381: include the unconfirmed paired transfer. Skip if
        // existingMatches already covers it (e.g. the user already confirmed
        // and reopened the drawer).
        if (pendingPairedTransaction) {
            const pendingStub = transactionToMatchableItem({
                transaction: pendingPairedTransaction,
                isCashBasis: ledgerBasis === AccountingLedgerBasis.cash,
            });
            if (!existingStubs.some((s) => s.id === pendingStub.id)) {
                existingStubs.push(pendingStub);
            }
        }

        // Only the existing stubs are persisted; `suggestedItems` are unsaved
        // accrual-review suggestions and must not seed the footer's "unchanged"
        // baseline (otherwise opening from a suggestion disables confirmation).
        const persistedIds = existingStubs.map((i) => i.id);

        if (existingStubs.length === 0) {
            return {
                mergedInitialItems: suggestedWithDocuments,
                persistedItemIds: persistedIds,
            };
        }
        const existingIds = new Set(persistedIds);
        const extras = (suggestedWithDocuments ?? []).filter(
            (i) => !existingIds.has(i.id),
        );
        return {
            mergedInitialItems: [...existingStubs, ...extras],
            persistedItemIds: persistedIds,
        };
    }, [
        existingMatches,
        suggestedWithDocuments,
        transaction,
        pendingPairedTransaction,
        ledgerBasis,
    ]);

    const enrichedItems = useEnrichedSuggestedItems(
        mergedInitialItems,
        currentTxMatchAmounts,
    );

    const [editAllocationSourceItem, setEditAllocationSourceItem] =
        useState<MatchableItem | null>(null);

    const handleOpenEditAllocation = useCallback(
        (sourceItem: MatchableItem) => {
            setEditAllocationSourceItem(sourceItem);
        },
        [],
    );

    const handleCloseEditAllocation = useCallback(() => {
        setEditAllocationSourceItem(null);
    }, []);

    const handleSaveSuccess = useCallback(
        (response: AccrualReviewApproveResponse) => {
            // applyApproveToAccrualReviewCache has already updated the accrual
            // review list cache in place. Skip the list invalidation so a
            // refetch doesn't drop the just-approved transaction from views
            // filtered by `isNotAccrualReviewed: true`.
            applyApproveToAccrualReviewCache(transactionId, response);
            invalidateAccrualMatchCaches(queryClient, {
                transactionId,
                skipAccrualReviewList: true,
            });
            setEditAllocationSourceItem(null);
            onClose();
        },
        [transactionId, onClose, queryClient],
    );

    return (
        <AccrualMatchProvider
            sourceTransaction={transaction}
            initialSelectedItems={enrichedItems}
            persistedItemIds={persistedItemIds}
            currentTxLineItemMatchAmounts={currentTxMatchAmounts}
            ledgerBasis={ledgerBasis}
            isGlFirst={isGlFirst}
            onOpenEditAllocation={handleOpenEditAllocation}
            onConfirmSuccess={handleSaveSuccess}
            onClose={onClose}
        >
            <Drawer
                open={open}
                onClose={onClose}
                size="fullScreen"
                noPadding
                headerAddonLeft={
                    <TransactionMatchDrawerTitle
                        hasAccrualLedger={hasAccrualLedger}
                    />
                }
            >
                <AccrualMatchPage transaction={transaction} />
            </Drawer>
            <EditAllocationDrawer
                open={!!editAllocationSourceItem}
                onClose={handleCloseEditAllocation}
                sourceItem={editAllocationSourceItem}
                onSaveSuccess={handleSaveSuccess}
            />
        </AccrualMatchProvider>
    );
};
